import { useCallback, useMemo, useState } from 'react'
import {
  always,
  compose,
  find,
  ifElse,
  isEmpty,
  map,
  prop,
  union,
  without,
} from 'ramda'
import { useSubscriberGroupsQuery } from './queries/useSubscriberGroupsQuery'

const DEFAULT_META = {
  currentPage: 1,
  lastPage: undefined,
}

const DEFAULT_QUERY_PARAMS = {
  limit: 20,
  search: '',
  shouldFetch: false,
}

export default function useSelectGroup() {
  const [selectedTags, setSelectedTagsState] = useState([])
  const [queryParams, setQueryParams] = useState(DEFAULT_QUERY_PARAMS)

  const shouldFetchGroups = queryParams.shouldFetch

  const {
    data,
    error,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useSubscriberGroupsQuery({
    limit: queryParams.limit,
    search: queryParams.search,
    enabled: shouldFetchGroups,
  })

  const groups = useMemo(() => {
    if (!data?.pages?.length) {
      return []
    }

    return data.pages.reduce((acc, page) => {
      if (Array.isArray(page?.data)) {
        acc.push(...page.data)
      }
      return acc
    }, [])
  }, [data])

  const meta = useMemo(() => {
    if (!data?.pages?.length) {
      return DEFAULT_META
    }

    return data.pages[data.pages.length - 1]?.meta ?? DEFAULT_META
  }, [data])

  const state = useMemo(
    () => ({
      groups,
      isLoading: isFetching,
      isLoadmore: isFetchingNextPage,
      error,
      selectedTags,
      meta,
    }),
    [error, groups, isFetchingNextPage, isFetching, meta, selectedTags],
  )

  const handleParentCheckboxChange = (itemId) => {
    const tags = compose(
      map(prop('id')),
      ifElse(isEmpty, always([]), prop('tags')),
      find((item) => item.id === itemId),
    )(state.groups)

    const isContained = tags.every((item) => state.selectedTags.includes(item))

    const updatedTags = isContained
      ? without(tags, state.selectedTags)
      : union(tags, state.selectedTags)

    setSelectedTagsState(updatedTags)
  }

  const handleTagCheckboxChange = (tagId) => {
    const isTagSelected = state.selectedTags.includes(tagId)

    const updatedTags = isTagSelected
      ? state.selectedTags.filter((id) => id !== tagId)
      : [...state.selectedTags, tagId]

    setSelectedTagsState(updatedTags)
  }

  const setSelectedTags = useCallback((value = []) => {
    setSelectedTagsState(value)
  }, [])

  const onDefaultValue = useCallback(
    (value) => {
      setSelectedTags(value)
    },
    [setSelectedTags],
  )

  const onFetchGroups = useCallback(({ limit, search = '' }) => {
    const normalizedSearch = search ?? ''

    setQueryParams((prev) => {
      const isSameConfig =
        prev.limit === limit &&
        prev.search === normalizedSearch &&
        prev.shouldFetch

      if (isSameConfig) {
        return prev
      }

      return {
        limit,
        search: normalizedSearch,
        shouldFetch: true,
      }
    })
  }, [])

  const loadMore = useCallback(
    (_params) => {
      if (hasNextPage) {
        fetchNextPage()
      }
    },
    [fetchNextPage, hasNextPage],
  )

  const onSearchChange = useCallback(
    ({ limit, keyword }) => {
      onFetchGroups({
        limit,
        search: keyword ?? '',
      })
    },
    [onFetchGroups],
  )

  const clearState = useCallback(() => {
    setSelectedTagsState([])
    setQueryParams(DEFAULT_QUERY_PARAMS)
  }, [])

  const checkAllByGroup = (groupId) => {
    const group = state.groups.find((group) => group.id === groupId)
    const tagIds = group?.tags?.map((tag) => tag.id) ?? []

    return tagIds.length
      ? tagIds.every((element) => state.selectedTags.includes(element))
      : false
  }

  return {
    state,
    onDefaultValue,
    setSelectedTags,
    onFetchGroups,
    handleParentCheckboxChange,
    handleTagCheckboxChange,
    loadMore,
    onSearchChange,
    clearState,
    checkAllByGroup,
  }
}
